import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getAllCompanies } from '../queries/companies';
import {
  clearConversationHistory,
  confirmOutboundMessage,
  conversationHistoryKey,
  getConversationHistory,
  reconcileConversationHistory,
  writeEngageMessage,
} from '../queries/conversationHistory';
import { getAllProducts } from '../queries/products';
import { getProspectSubmissions } from '../queries/prospectSubmissions';
import EngageWorkspace from '../components/Engage/EngageWorkspace';
import SwitchProspectDialog from '../components/Engage/SwitchProspectDialog';
import EngageConfirmDialog from '../components/Engage/EngageConfirmDialog';
import {
  ENGAGE_AVAILABILITY,
  ENGAGE_COPY,
  getEngageAvailability,
} from '../components/ClientWorkspace/engageAvailability';
import {
  CONTROL_STATE,
  EMPTY_RELATIONSHIP,
  GENERATE_ANOTHER_INFO,
  GENERATE_INFO,
  appLeaveHref,
  approvedProspectsForOpportunity,
  applyReconcileResult,
  captureManualVersion,
  generateMessageControl,
  hasNewCommunication,
  hasUnsavedEngageWork,
  hasUnsentMessage,
  historyForProspect,
  historyLoadStatus,
  historyMessagesValid,
  historyRevision,
  isHistoryDirty,
  opportunityLabels,
  refineMessageControl,
  relationshipSessionValues,
  resolveSelectedProspectId,
  selectProspectResult,
  writingFollowUp,
} from '../components/Engage/engageSession';
import { setNavigationGuard } from '../utils/navigationGuard';

const MISMATCH_BODY =
  "Some of the names, company details or content don't clearly match this Prospect. Please check before continuing.";
const CLEAR_HISTORY_BODY =
  'This will permanently delete all saved messages for this prospect, including copied and saved messages. This action cannot be undone.';
const FAILURE_BODY =
  "We couldn't process this communication. Nothing was changed and your current work is still here. Please try again.";
const WRITE_FAILURE_BODY =
  "We couldn't create the new message. Your current message and other work haven't changed. Please try again.";
const LEAVE_BODY = 'You have work in this Engage session that will be discarded if you leave.';

const EngageNotice = ({ title, body, actionLabel, onAction }) => (
  <Box maxWidth={560} mx='auto' mt={8} px={3} textAlign='center'>
    <Typography variant='h5' fontWeight={700} mb={1}>
      {title}
    </Typography>
    <Typography variant='body2' color='text.secondary' mb={3}>
      {body}
    </Typography>
    {actionLabel ? (
      <Button variant='contained' onClick={onAction} sx={{ textTransform: 'none', fontWeight: 600 }}>
        {actionLabel}
      </Button>
    ) : null}
  </Box>
);

const EngagePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const clientId = params.get('clientId') || '';
  const programId = params.get('programId') || '';
  const attemptRef = useRef(null);
  const allowNavigationRef = useRef(false);
  const historyGenerationRef = useRef(0);
  const prospectRef = useRef('');
  const pendingWriteRef = useRef(null);
  const baselineRef = useRef('');
  const lastWriteRef = useRef(null);

  const {
    data: prospects,
    isLoading,
    isError,
  } = useQuery(
    getProspectSubmissions({
      config: { staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: true, refetchInterval: 30000 },
    }),
  );
  const { data: companies } = useQuery(getAllCompanies({}));
  const { data: products } = useQuery(getAllProducts({}));

  const approved = useMemo(
    () => approvedProspectsForOpportunity(prospects, clientId, programId),
    [prospects, clientId, programId],
  );
  const labels = useMemo(
    () => opportunityLabels(prospects, clientId, programId, companies, products),
    [prospects, clientId, programId, companies, products],
  );

  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(null);
  const [historyDraft, setHistoryDraft] = useState(null);
  const [newCommunication, setNewCommunication] = useState('');
  const [pendingSwitchId, setPendingSwitchId] = useState('');
  const [pendingLeave, setPendingLeave] = useState('');
  const [proposalId, setProposalId] = useState('');
  const [failureOpen, setFailureOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [refineText, setRefineText] = useState('');
  const [messageText, setMessageText] = useState('');
  const [messageBaseline, setMessageBaseline] = useState('');
  const [confirmedText, setConfirmedText] = useState(null);
  const [versions, setVersions] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [writingFailureOpen, setWritingFailureOpen] = useState(false);

  const resolvedId = resolveSelectedProspectId(selectedId, approved);
  prospectRef.current = resolvedId;
  baselineRef.current = messageBaseline;
  const selectedProspect = approved.find((row) => String(row._id) === resolvedId) || null;
  const relationship =
    draft?.prospectId === resolvedId
      ? draft.values
      : selectedProspect
        ? relationshipSessionValues(selectedProspect)
        : EMPTY_RELATIONSHIP;

  useEffect(() => {
    if (resolvedId !== selectedId) setSelectedId(resolvedId);
  }, [resolvedId, selectedId]);

  useEffect(() => {
    setDraft(null);
    setHistoryDraft(null);
    setNewCommunication('');
    setRefineText('');
    setProposalId('');
    setFailureOpen(false);
    setClearOpen(false);
    setMessageText('');
    setMessageBaseline('');
    setConfirmedText(null);
    setVersions([]);
    setHasGenerated(false);
    setCopyOpen(false);
    setWritingFailureOpen(false);
    pendingWriteRef.current = null;
    lastWriteRef.current = null;
  }, [resolvedId]);

  useEffect(() => {
    if (pendingSwitchId && !approved.some((row) => String(row._id) === pendingSwitchId)) {
      setPendingSwitchId('');
    }
  }, [approved, pendingSwitchId]);

  const historyQuery = useQuery(getConversationHistory(resolvedId));
  const savedHistory = historyForProspect(historyQuery.data, resolvedId);
  const savedRevision = historyRevision(historyQuery.data);
  const historyItems = historyDraft?.prospectId === resolvedId ? historyDraft.messages : savedHistory;
  const historyDirty = historyDraft?.prospectId === resolvedId && isHistoryDirty(historyDraft.messages, savedHistory);
  const historyStatus = historyLoadStatus({
    prospectSelected: Boolean(resolvedId),
    data: historyQuery.data,
    isError: historyQuery.isError,
  });

  const availability = getEngageAvailability(prospects, clientId, programId);
  const onWorkspace = Boolean(
    clientId && programId && !isLoading && !isError && availability === ENGAGE_AVAILABILITY.APPROVED,
  );
  const unsaved =
    onWorkspace &&
    hasUnsavedEngageWork({
      relationship,
      prospect: selectedProspect,
      history: historyItems,
      savedHistory,
      newCommunication,
      messageText,
      confirmedText,
    });

  useEffect(() => {
    if (!unsaved) return undefined;
    return setNavigationGuard((href) => {
      setPendingLeave(href);
      return false;
    });
  }, [unsaved]);

  useEffect(() => {
    if (!unsaved) return undefined;
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a');
      if (!anchor || allowNavigationRef.current) return;
      const href = appLeaveHref(
        { href: anchor.href, target: anchor.target, download: anchor.hasAttribute('download') },
        window.location.href,
      );
      if (!href) return;
      event.preventDefault();
      setPendingLeave(href);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [unsaved]);

  const versionPayload = (list) => (list || []).map((version) => ({ title: version.title, message: version.message }));

  const runWrite = (payload) => {
    lastWriteRef.current = payload;
    write.mutate(payload);
  };

  const continueWrite = (pending, history) => {
    if (!pending || pending.prospectId !== prospectRef.current) return;
    const captured =
      pending.mode === 'generate'
        ? { versions: pending.versions, baselineText: baselineRef.current }
        : captureManualVersion(pending.versions, pending.message, baselineRef.current);
    setVersions(captured.versions);
    setMessageBaseline(captured.baselineText);
    runWrite({
      prospectId: pending.prospectId,
      mode: pending.mode,
      refineInstruction: pending.refineInstruction || '',
      relationship: pending.relationship,
      message: pending.message,
      versions: captured.versions,
      versionHistory: versionPayload(captured.versions),
      messages: history,
      newCommunication: '',
      reconcileAttempts: pending.reconcileAttempts || 0,
    });
  };

  const reconcile = useMutation({
    mutationFn: (request) => {
      const payload = { ...request };
      delete payload.generation;
      delete payload.writeAfter;
      delete payload.reconcileAttempts;
      return reconcileConversationHistory(payload).then((response) => response.data);
    },
    onSuccess: (data, variables) => {
      if (variables.generation !== historyGenerationRef.current) return;
      const result = applyReconcileResult({
        selectedProspectId: prospectRef.current,
        requestProspectId: variables.prospectId,
        data,
      });
      if (result.type === 'warning') {
        setProposalId(result.proposalId);
        setFailureOpen(false);
        return;
      }
      if (result.type !== 'applied') return;
      const history = (result.history || []).map((message) => ({
        ...message,
        prospectId: message?.prospectId || String(result.prospectId || ''),
      }));
      queryClient.setQueryData(conversationHistoryKey(result.prospectId), {
        revision: result.revision,
        messages: history,
      });
      if (!result.updateSession) return;
      setHistoryDraft(null);
      setNewCommunication('');
      setProposalId('');
      setFailureOpen(false);
      const pending = writingFollowUp(variables.writeAfter || pendingWriteRef.current, prospectRef.current);
      pendingWriteRef.current = null;
      if (pending) {
        continueWrite(pending, history);
        return;
      }
      toast.success('Conversation history updated');
    },
    onError: (error, variables) => {
      if (variables.generation !== historyGenerationRef.current) return;
      if (String(variables.prospectId || '') !== String(prospectRef.current || '')) return;
      const status = error?.response?.status;
      if (status === 400 || status === 409) {
        pendingWriteRef.current = null;
        toast.error(error.response?.data?.message || 'Check the conversation and try again.');
        if (status === 409) {
          queryClient.invalidateQueries({ queryKey: conversationHistoryKey(variables.prospectId) });
        }
        return;
      }
      setFailureOpen(true);
    },
  });

  const clearHistory = useMutation({
    mutationFn: (prospectId) => clearConversationHistory(prospectId).then((response) => response.data),
    onSuccess: (data, prospectId) => {
      historyGenerationRef.current += 1;
      attemptRef.current = null;
      pendingWriteRef.current = null;
      queryClient.setQueryData(conversationHistoryKey(prospectId), {
        revision: Number.isInteger(data?.revision) ? data.revision : 0,
        messages: [],
      });
      if (String(prospectId) !== String(prospectRef.current || '')) return;
      setHistoryDraft(null);
      setProposalId('');
      setFailureOpen(false);
      setClearOpen(false);
      toast.success('Conversation history cleared');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Couldn't clear conversation history");
    },
  });

  const write = useMutation({
    mutationFn: (payload) => writeEngageMessage(payload).then((response) => response.data),
    onSuccess: (data, variables) => {
      if (variables.prospectId !== prospectRef.current) return;
      if (!String(data?.message || '').trim()) {
        setWritingFailureOpen(true);
        return;
      }
      setWritingFailureOpen(false);
      setMessageText(data.message);
      setMessageBaseline(data.message);
      setHasGenerated(true);
      setVersions([
        {
          id: `ai-${Date.now()}`,
          title: data.versionTitle,
          message: data.message,
          source: 'ai',
          createdAt: new Date().toISOString(),
        },
        ...(variables.versions || []),
      ]);
    },
    onError: (error, variables) => {
      if (variables?.prospectId !== prospectRef.current) return;
      const status = error?.response?.status;
      if (status === 409) {
        const attempts = variables.reconcileAttempts || 0;
        if (attempts >= 1) {
          setWritingFailureOpen(true);
          return;
        }
        const followUp = {
          prospectId: variables.prospectId,
          mode: variables.mode,
          refineInstruction: variables.refineInstruction,
          relationship: variables.relationship,
          message: variables.message,
          versions: variables.versions || [],
          reconcileAttempts: attempts + 1,
        };
        pendingWriteRef.current = followUp;
        runReconcile({
          prospectId: variables.prospectId,
          revision: historyRevision(queryClient.getQueryData(conversationHistoryKey(variables.prospectId))),
          relationship: variables.relationship,
          messages: variables.messages,
          newCommunication: variables.newCommunication,
          writeAfter: followUp,
        });
        return;
      }
      if (status === 400) {
        toast.error(error.response?.data?.message || 'Check the message and try again.');
        return;
      }
      setWritingFailureOpen(true);
    },
  });

  const confirmSent = useMutation({
    mutationFn: (payload) => confirmOutboundMessage(payload).then((response) => response.data),
    onSuccess: (history, variables) => {
      queryClient.setQueryData(conversationHistoryKey(variables.prospectId), history);
      if (variables.prospectId !== prospectRef.current) return;
      setHistoryDraft(null);
      setConfirmedText(variables.text);
    },
    onError: (error, variables) => {
      if (variables?.prospectId !== prospectRef.current) return;
      toast.error(error.response?.data?.message || "Couldn't save the sent message.");
    },
  });

  const runReconcile = (payload) => {
    const request = { ...payload, generation: historyGenerationRef.current };
    attemptRef.current = request;
    reconcile.mutate(request);
  };

  const requestSelect = (nextId) => {
    if (reconcile.isPending || clearHistory.isPending || write.isPending || confirmSent.isPending) return;
    const result = selectProspectResult({
      nextId,
      selectedId: resolvedId,
      draft: relationship,
      prospect: selectedProspect,
      historyDirty,
      newCommunication,
      messageUnsent: hasUnsentMessage(messageText, confirmedText),
    });
    if (result.type === 'confirm') setPendingSwitchId(result.nextId);
    if (result.type === 'switch') setSelectedId(result.nextId);
  };

  const confirmSwitch = () => {
    setSelectedId(pendingSwitchId);
    setPendingSwitchId('');
  };

  const confirmLeave = () => {
    const href = pendingLeave;
    allowNavigationRef.current = true;
    setPendingLeave('');
    navigate(href);
  };

  const beginWrite = (mode, instruction = '') => {
    const refineInstruction = typeof instruction === 'string' ? instruction.trim() : '';
    if (mode === 'refine' && !refineInstruction) return;
    if (!historyMessagesValid(historyItems)) {
      toast.error('Add text to every conversation history message first.');
      return;
    }
    if (mode === 'refine') setRefineText('');
    const needsReconcile = historyDirty || hasNewCommunication(newCommunication);
    const followUp = {
      prospectId: resolvedId,
      mode,
      refineInstruction,
      relationship,
      message: messageText,
      versions,
      reconcileAttempts: 0,
    };
    if (needsReconcile) {
      pendingWriteRef.current = followUp;
      runReconcile({
        prospectId: resolvedId,
        revision: savedRevision,
        relationship,
        messages: historyItems,
        newCommunication,
        writeAfter: followUp,
      });
      return;
    }
    const captured =
      mode === 'generate'
        ? { versions, baselineText: messageBaseline }
        : captureManualVersion(versions, messageText, messageBaseline);
    setVersions(captured.versions);
    setMessageBaseline(captured.baselineText);
    runWrite({
      prospectId: resolvedId,
      mode,
      refineInstruction,
      relationship,
      message: messageText,
      versions: captured.versions,
      versionHistory: versionPayload(captured.versions),
      messages: historyItems,
      newCommunication: '',
      reconcileAttempts: 0,
    });
  };

  const onGenerate = () => beginWrite(hasGenerated ? 'generateAnother' : 'generate');

  const onRefine = (instruction) => beginWrite('refine', typeof instruction === 'string' ? instruction : refineText);

  const onSelectVersion = (versionId) => {
    const version = versions.find((item) => item.id === versionId);
    if (!version) return;
    const captured = captureManualVersion(versions, messageText, messageBaseline);
    setVersions(captured.versions);
    setMessageText(version.message);
    setMessageBaseline(version.message);
  };

  const onConfirmCopy = () => {
    const captured = captureManualVersion(versions, messageText, messageBaseline);
    setVersions(captured.versions);
    setMessageBaseline(captured.baselineText);
    setCopyOpen(false);
    const text = messageText;
    navigator.clipboard.writeText(text).catch(() => {
      toast.error('Could not copy the message to the clipboard.');
    });
    confirmSent.mutate({ prospectId: resolvedId, text });
  };

  const onAddAnyway = () => {
    setProposalId('');
    runReconcile({
      prospectId: resolvedId,
      revision: savedRevision,
      proposalId,
      writeAfter: pendingWriteRef.current || attemptRef.current?.writeAfter || null,
    });
  };

  const onTryAgain = () => {
    setFailureOpen(false);
    if (attemptRef.current) runReconcile(attemptRef.current);
  };

  const onTryWriteAgain = () => {
    setWritingFailureOpen(false);
    const last = lastWriteRef.current;
    if (!last) return;
    if (historyDirty || hasNewCommunication(newCommunication) || last.message !== messageText) {
      beginWrite(last.mode, last.refineInstruction);
      return;
    }
    runWrite(last);
  };

  if (!clientId || !programId) {
    return (
      <EngageNotice
        title='Engage'
        body='Open an opportunity and choose Engage from the AI Assistant.'
        actionLabel='Back to opportunities'
        onAction={() => navigate('/opportunities')}
      />
    );
  }

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' pt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <EngageNotice
        title='Engage'
        body='Could not load prospects for this opportunity.'
        actionLabel='Back to opportunities'
        onAction={() => navigate('/opportunities')}
      />
    );
  }

  if (availability !== ENGAGE_AVAILABILITY.APPROVED) {
    const pending = availability === ENGAGE_AVAILABILITY.PENDING;
    return (
      <EngageNotice
        title='Engage'
        body={ENGAGE_COPY[availability]}
        actionLabel={pending ? 'View prospects' : 'Add prospect'}
        onAction={() => navigate('/prospects')}
      />
    );
  }

  const historyReady = historyStatus === 'ready';
  const refineReady = refineMessageControl(Boolean(selectedProspect), messageText) === CONTROL_STATE.ELIGIBLE;
  const busy = reconcile.isPending || clearHistory.isPending || write.isPending || confirmSent.isPending;
  const generateLabel = reconcile.isPending
    ? 'Updating history...'
    : write.isPending && write.variables?.mode !== 'refine'
      ? 'Generating...'
      : hasGenerated
        ? 'Generate another'
        : 'Generate message';

  return (
    <>
      <EngageWorkspace
        clientName={labels.clientName}
        productName={labels.productName}
        prospects={approved}
        selectedProspect={selectedProspect}
        relationship={relationship}
        onRelationshipChange={(key, value) => {
          setDraft({ prospectId: resolvedId, values: { ...relationship, [key]: value } });
        }}
        onSelectProspect={requestSelect}
        historyItems={historyItems}
        historyStatus={historyStatus}
        historyLocked={busy}
        onHistoryChange={(messages) => setHistoryDraft({ prospectId: resolvedId, messages })}
        onClearHistory={() => setClearOpen(true)}
        newCommunication={newCommunication}
        onNewCommunicationChange={setNewCommunication}
        generateState={generateMessageControl(Boolean(selectedProspect), historyReady, hasGenerated, messageText)}
        generatePending={
          reconcile.isPending || clearHistory.isPending || (write.isPending && write.variables?.mode !== 'refine')
        }
        generateLabel={generateLabel}
        generateInfo={hasGenerated ? GENERATE_ANOTHER_INFO : GENERATE_INFO}
        onGenerate={onGenerate}
        refineState={refineMessageControl(Boolean(selectedProspect), messageText)}
        refineActionState={
          refineReady && String(refineText || '').trim() ? CONTROL_STATE.ELIGIBLE : CONTROL_STATE.DISABLED
        }
        refinePending={write.isPending && write.variables?.mode === 'refine'}
        refineText={refineText}
        onRefineTextChange={setRefineText}
        onRefine={onRefine}
        messageText={messageText}
        onMessageTextChange={setMessageText}
        messageEditable={hasGenerated}
        onRequestCopy={() => setCopyOpen(true)}
        versions={versions}
        onSelectVersion={onSelectVersion}
      />
      <SwitchProspectDialog
        open={Boolean(pendingSwitchId)}
        onKeepWorking={() => setPendingSwitchId('')}
        onSwitch={confirmSwitch}
      />
      <EngageConfirmDialog
        open={Boolean(pendingLeave)}
        title='Unsaved work'
        body={LEAVE_BODY}
        cancelLabel='Keep working'
        confirmLabel='Leave without saving'
        onCancel={() => setPendingLeave('')}
        onConfirm={confirmLeave}
      />
      <EngageConfirmDialog
        open={Boolean(proposalId)}
        title={`This communication may not belong to ${selectedProspect?.company || 'this company'}`}
        body={MISMATCH_BODY}
        cancelLabel='Cancel'
        confirmLabel='Add anyway'
        onCancel={() => {
          pendingWriteRef.current = null;
          setProposalId('');
        }}
        onConfirm={onAddAnyway}
      />
      <EngageConfirmDialog
        open={clearOpen}
        title='Clear conversation history?'
        body={CLEAR_HISTORY_BODY}
        cancelLabel='Cancel'
        confirmLabel='Clear history'
        confirmColor='error'
        confirmPending={clearHistory.isPending}
        icon={
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#fdecec',
              color: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DeleteOutlineIcon fontSize='small' />
          </Box>
        }
        onCancel={() => setClearOpen(false)}
        onConfirm={() => clearHistory.mutate(resolvedId)}
      />
      <EngageConfirmDialog
        open={failureOpen}
        title="Couldn't update conversation history"
        body={FAILURE_BODY}
        cancelLabel='Close'
        confirmLabel='Try again'
        onCancel={() => {
          pendingWriteRef.current = null;
          setFailureOpen(false);
        }}
        onConfirm={onTryAgain}
      />
      <EngageConfirmDialog
        open={writingFailureOpen}
        title="Couldn't update message"
        body={WRITE_FAILURE_BODY}
        cancelLabel='Close'
        confirmLabel='Try again'
        onCancel={() => setWritingFailureOpen(false)}
        onConfirm={onTryWriteAgain}
      />
      <EngageConfirmDialog
        open={copyOpen}
        title='Confirm sent message'
        body='Are you planning to send this message to the prospect unchanged?'
        cancelLabel='Not yet'
        confirmLabel='Yes, copy & save'
        onCancel={() => setCopyOpen(false)}
        onConfirm={onConfirmCopy}
      />
    </>
  );
};

export default EngagePage;
